import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Monitor, Mail, MapPin, Phone, ArrowRight, Check, AlertCircle } from 'lucide-react';
import { BUSINESS, subscribeToComingSoon } from '../config';

const initialForm = { firstName: '', lastName: '', email: '', gdpr: false };

export default function ComingSoon() {
  const [form, setForm] = useState(initialForm);
  const [status, setStatus] = useState('idle'); // idle | submitting | success | error
  const [error, setError] = useState('');

  const onChange = (field) => (e) => {
    const value = field === 'gdpr' ? e.target.checked : e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setError('');

    if (!form.firstName.trim()) return setError('Indiquez votre prénom.');
    if (!form.lastName.trim()) return setError('Indiquez votre nom.');
    if (!form.email.trim()) return setError('Indiquez votre email.');
    if (!form.gdpr) return setError('Vous devez accepter d’être contacté à l’ouverture.');

    setStatus('submitting');
    try {
      await subscribeToComingSoon({
        email: form.email.trim(),
        first_name: form.firstName.trim(),
        last_name: form.lastName.trim(),
        gdpr_consent: true,
      });
      setStatus('success');
    } catch (err) {
      setStatus('error');
      setError(err?.message || 'Erreur lors de l’enregistrement. Réessayez plus tard.');
    }
  };

  return (
    <div className="min-h-screen bg-primary text-white" style={{ fontFamily: 'DM Sans, sans-serif' }}>
      {/* Fond dégradé subtil */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-secondary/10 blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-[600px] h-[600px] rounded-full bg-secondary/5 blur-3xl" />
      </div>

      <div className="relative max-w-6xl mx-auto px-6 sm:px-10 py-10 md:py-16 min-h-screen flex flex-col">
        {/* Header logo */}
        <header className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-sm bg-secondary flex items-center justify-center shadow-[0_0_30px_rgba(245,158,11,0.35)]">
              <Monitor className="w-6 h-6 text-black" />
            </div>
            <div>
              <div className="font-bold text-lg tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {BUSINESS.name}
              </div>
              <div className="text-[10px] text-slate-500 uppercase tracking-[0.18em]">Boutique</div>
            </div>
          </div>
          <Link
            to="/compte/connexion"
            className="hidden sm:inline-flex items-center gap-2 text-sm text-slate-600 hover:text-secondary transition-colors"
            data-testid="coming-soon-login-link"
          >
            J'ai déjà un compte
            <ArrowRight className="w-4 h-4" />
          </Link>
        </header>

        {/* Hero */}
        <main className="flex-1 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center py-12 md:py-20">
          <div className="animate-fade-in-up">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-secondary/10 border border-secondary/40 text-secondary text-xs uppercase tracking-widest mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-secondary animate-pulse" />
              Bientôt disponible
            </div>

            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Votre nouvelle{' '}
              <span className="text-secondary">boutique informatique</span> à Meaux
            </h1>

            <p className="mt-6 text-base sm:text-lg text-slate-600 leading-relaxed max-w-xl">
              Du matériel neuf et reconditionné, sélectionné et garanti par l'équipe Météris.
              Vente, conseil, retrait en boutique au cœur de Meaux —{' '}
              <span className="text-gray-200">notre site marchand ouvre très prochainement.</span>
            </p>

            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-xl">
              <div className="flex items-start gap-3 p-4 rounded-sm bg-white border border-white/10">
                <MapPin className="w-5 h-5 text-secondary mt-0.5 flex-shrink-0" />
                <div className="text-sm">
                  <div className="text-gray-200 font-medium">{BUSINESS.adresse}</div>
                  <div className="text-slate-500 text-xs mt-0.5">Retrait sur place</div>
                </div>
              </div>
              <div className="flex items-start gap-3 p-4 rounded-sm bg-white border border-white/10">
                <Phone className="w-5 h-5 text-secondary mt-0.5 flex-shrink-0" />
                <div className="text-sm">
                  <a
                    href={`tel:${BUSINESS.telephoneRaw}`}
                    className="text-gray-200 font-medium hover:text-secondary transition-colors"
                  >
                    {BUSINESS.telephone}
                  </a>
                  <div className="text-slate-500 text-xs mt-0.5">Lun-Ven · 16h30-18h00</div>
                </div>
              </div>
            </div>
          </div>

          {/* Formulaire */}
          <div className="animate-fade-in-up" style={{ animationDelay: '120ms' }}>
            <div className="bg-white border border-white/10 rounded-sm p-6 sm:p-8 shadow-2xl">
              {status === 'success' ? (
                <div className="text-center py-8" data-testid="coming-soon-success">
                  <div className="w-14 h-14 rounded-full bg-secondary/10 border border-secondary/40 flex items-center justify-center mx-auto">
                    <Check className="w-7 h-7 text-secondary" />
                  </div>
                  <h2
                    className="mt-5 text-2xl font-bold text-white"
                    style={{ fontFamily: 'Outfit, sans-serif' }}
                  >
                    Merci {form.firstName} !
                  </h2>
                  <p className="mt-3 text-slate-600 text-sm leading-relaxed">
                    Vous serez prévenu(e) par email à <span className="text-secondary">{form.email}</span>{' '}
                    dès l'ouverture de la boutique. Aucun spam — juste un mail d'annonce.
                  </p>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-2 text-secondary mb-2">
                    <Mail className="w-4 h-4" />
                    <span className="text-xs uppercase tracking-widest font-medium">
                      Prévenez-moi à l'ouverture
                    </span>
                  </div>
                  <h2
                    className="text-2xl font-bold text-white"
                    style={{ fontFamily: 'Outfit, sans-serif' }}
                  >
                    Recevez le mail de lancement
                  </h2>
                  <p className="mt-2 text-sm text-slate-600">
                    Un seul email, le jour où le site ouvre.
                  </p>

                  <form onSubmit={submit} className="mt-6 space-y-4" data-testid="coming-soon-form">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-slate-600 mb-1.5 uppercase tracking-wider">
                          Prénom
                        </label>
                        <input
                          type="text"
                          value={form.firstName}
                          onChange={onChange('firstName')}
                          className="w-full bg-white/5 border border-white/15 rounded-sm px-3 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-secondary/60 focus:ring-1 focus:ring-secondary/40 transition"
                          placeholder="Marie"
                          required
                          data-testid="coming-soon-first-name"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-slate-600 mb-1.5 uppercase tracking-wider">
                          Nom
                        </label>
                        <input
                          type="text"
                          value={form.lastName}
                          onChange={onChange('lastName')}
                          className="w-full bg-white/5 border border-white/15 rounded-sm px-3 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-secondary/60 focus:ring-1 focus:ring-secondary/40 transition"
                          placeholder="Dupont"
                          required
                          data-testid="coming-soon-last-name"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs text-slate-600 mb-1.5 uppercase tracking-wider">
                        Email
                      </label>
                      <input
                        type="email"
                        value={form.email}
                        onChange={onChange('email')}
                        className="w-full bg-white/5 border border-white/15 rounded-sm px-3 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-secondary/60 focus:ring-1 focus:ring-secondary/40 transition"
                        placeholder="marie.dupont@exemple.fr"
                        required
                        data-testid="coming-soon-email"
                      />
                    </div>

                    <label className="flex items-start gap-3 text-xs text-slate-600 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={form.gdpr}
                        onChange={onChange('gdpr')}
                        className="mt-0.5 w-4 h-4 rounded border-white/10 bg-slate-50 text-secondary focus:ring-secondary/40 focus:ring-offset-0"
                        data-testid="coming-soon-gdpr"
                      />
                      <span className="leading-relaxed">
                        J'accepte d'être contacté(e) par {BUSINESS.name} à l'ouverture du site.
                        Mes données ne seront pas transmises à un tiers. Voir notre{' '}
                        <Link
                          to="/confidentialite"
                          className="text-secondary hover:underline"
                        >
                          politique de confidentialité
                        </Link>
                        .
                      </span>
                    </label>

                    {error && (
                      <div className="flex items-start gap-2 p-3 rounded-sm bg-rose-50 border border-red-500/30 text-rose-600 text-xs">
                        <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                        <span>{error}</span>
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={status === 'submitting'}
                      className="w-full bg-secondary hover:bg-secondary-hover text-white font-bold uppercase tracking-wider rounded-sm py-3 text-sm transition-all shadow-lg disabled:opacity-60 disabled:cursor-not-allowed"
                      data-testid="coming-soon-submit"
                    >
                      {status === 'submitting' ? 'Enregistrement…' : 'Me prévenir à l’ouverture'}
                    </button>
                  </form>
                </>
              )}
            </div>

            <Link
              to="/compte/connexion"
              className="sm:hidden mt-4 block text-center text-sm text-slate-600 hover:text-secondary transition-colors"
            >
              J'ai déjà un compte — me connecter →
            </Link>
          </div>
        </main>

        {/* Footer */}
        <footer className="border-t border-white/10 pt-6 mt-8 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-500">
          <div>
            © {new Date().getFullYear()} {BUSINESS.name} — Une entreprise du groupe Météris
          </div>
          <div className="flex items-center gap-4">
            <Link to="/mentions-legales" className="hover:text-slate-700 transition-colors">
              Mentions légales
            </Link>
            <Link to="/confidentialite" className="hover:text-slate-700 transition-colors">
              Confidentialité
            </Link>
          </div>
        </footer>
      </div>
    </div>
  );
}
